<template>
  <div v-if="sortedGroup.length" class="notification-template">
    <div class="notification-templates-block">
      <div
        v-for="notification in sortedGroup"
        :key="notification.id"
        class="notification-template__template"
      >
        <div class="notification-template__field">
          <div class="notification-label">
            {{ $t('textLetterFor') }} <span>{{ notification.channel }}</span>
          </div>
          <VTextarea
            ref="textareaRefs"
            :ref-in-for="true"
            v-model="notification.template"
            outlined
            :error-messages="(errors[notification.id] && errors[notification.id].template) || []"
            @click="updateCursorPosition(notification.id, $event)"
            @keyup="updateCursorPosition(notification.id, $event)"
          />
        </div>

        <div class="notification-variables">
          <div class="font-weight-medium mb-2">
            {{ $t('insertVariable') }}
          </div>
          <div
            v-for="variable in variables"
            :key="variable.id"
            class="d-flex align-center"
          >
            <span>&larr;</span>
            <div
              class="ml-3 pseudo-link primary--text var-item"
              @click="insertVariable(variable, notification.id)"
              @mousedown="focusTextarea(notification.id)"
            >
              {{ variable.name }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="notification-template__buttons">
      <VBtn
        color="success"
        :loading="updateLoading"
        elevation="0"
        @click="update"
      >
        {{ $t('save') }}
      </VBtn>
    </div>
  </div>
</template>

<script>
import useValidation from '@/compositions/useValidation';
import NotificationTemplateModel from '../../orm/models/Company/NotificationTemplate/NotificationTemplate';

export default {
  name: 'NotificationTemplate',
  props: {
    group: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      updateLoading: false,
      cursorPositions: {},
      variables: [
        { id: 'company.name', name: this.$t('companyName') },
        { id: 'vacancy.title', name: this.$t('vacancyTitle') },
        { id: 'candidate.firstName', name: this.$t('candidateFirstName') },
        { id: 'candidate.lastName', name: this.$t('candidateLastName') },
        { id: 'hr.firstName', name: this.$t('myFirstName') },
        { id: 'hr.lastName', name: this.$t('myLastName') },
        { id: 'hr.phone', name: this.$t('myPhone') },
        { id: 'hr.email', name: this.$t('myEmail') },
        { id: 'interview.link', name: this.$t('interviewLink') },
      ],
      errors: {},
    };
  },
  computed: {
    sortedGroup() {
      return [...this.group].sort((a, b) => {
        if (a.channel === 'email') return -1;
        if (b.channel === 'email') return 1;
        return 0;
      });
    },
  },
  created() {
    const { errors } = useValidation();
    this.errors = errors;
  },
  methods: {
    updateCursorPosition(notificationId, event) {
      const inputEl = event.target;
      if (inputEl) {
        const start = inputEl.selectionStart || 0;
        const end = inputEl.selectionEnd || 0;
        
        this.cursorPositions[notificationId] = {
          start,
          end,
          timestamp: Date.now(),
        };
      }
    },
    focusTextarea(notificationId) {
      const notificationIndex = this.sortedGroup.findIndex((n) => n.id === notificationId);
      if (notificationIndex === -1) return;

      const textareaComponent = this.$refs.textareaRefs[notificationIndex];
      const inputEl = textareaComponent?.$refs?.input;

      if (inputEl) {
        inputEl.focus();
        
        if (this.cursorPositions[notificationId]) {
          const { start, end } = this.cursorPositions[notificationId];
          this.$nextTick(() => {
            inputEl.setSelectionRange(start, end);
          });
        }
      }
    },
    insertVariable(variable, notificationId) {
      const notificationIndex = this.sortedGroup.findIndex((n) => n.id === notificationId);
      if (notificationIndex === -1) return;

      const notification = this.sortedGroup[notificationIndex];
      const textareaComponent = this.$refs.textareaRefs[notificationIndex];
      const inputEl = textareaComponent?.$refs?.input;

      if (!inputEl) {
        return;
      }

      inputEl.focus();
      
      const text = `{${variable.id}}`;
      
      try {
        const start = inputEl.selectionStart || 0;
        const end = inputEl.selectionEnd || 0;
        
        inputEl.setSelectionRange(start, end);
        
        const success = document.execCommand('insertText', false, text);
        
        if (success) {
          const groupIndex = this.group.findIndex((n) => n.id === notificationId);
          if (groupIndex !== -1) {
            this.group[groupIndex].template = inputEl.value;
          }
          
          const newCursorPos = start + text.length;
          this.$nextTick(() => {
            inputEl.setSelectionRange(newCursorPos, newCursorPos);
          });
        } else {
          this.fallbackInsertion(notification, text, start, end, notificationId, inputEl);
        }
      } catch (error) {
        const start = inputEl.selectionStart || 0;
        const end = inputEl.selectionEnd || 0;
        this.fallbackInsertion(notification, text, start, end, notificationId, inputEl);
      }
    },
    
    fallbackInsertion(notification, text, start, end, notificationId, inputEl) {
      const before = notification.template.slice(0, start);
      const after = notification.template.slice(end);
      const newValue = before + text + after;

      const groupIndex = this.group.findIndex((n) => n.id === notificationId);
      if (groupIndex !== -1) {
        this.group[groupIndex].template = newValue;
      }

      const newCursorPos = start + text.length;
      this.$nextTick(() => {
        inputEl.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    async update() {
      this.updateLoading = true;
      await Promise.all(
        this.group.map((n) => NotificationTemplateModel.api().update(n.id, { template: n.template })),
      );
      this.$toast(this.$t('dataSaved'));
      this.updateLoading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-label {
  margin-bottom: 10px;
  font-size: 16px;
  line-height: 140%;
  color: rgba(33, 33, 33, 1);
  span {
    text-transform: uppercase;
  }
}

.notification-template {
  &__template {
    display: flex;
    @media screen and (max-width: 1024px) {
      flex-direction: column;
    }
  }
  &__field {
    width: 70%;
    margin-right: 40px;
    @media screen and (max-width: 1024px) {
      width: 100%;
      margin-right: 0;
    }
  }
}

.notification-templates-block {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 40px;
  @media screen and (max-width: 1024px) {
    margin-bottom: 40px;
  }
}

.notification-templates {
  width: 100%;
}

::v-deep {
  .v-textarea {
    .v-input__control {
      .v-input__slot {
        fieldset {
          border-radius: 10px;
        }
      }
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  insertVariable: Insert template variable
  save: Save
  companyName: Company name
  vacancyTitle: Position
  candidateFirstName: Candidate first name
  candidateLastName: Candidate last name
  myFirstName: My first name
  myLastName: My last name
  myPhone: My phone number
  myEmail: My e-mail
  interviewLink: Interview link
  vacancyLink: Job link
  textLetterFor: The text of the letter for
  dataSaved: The data is saved
</i18n>

<i18n locale="ru" lang="yaml">
  insertVariable: Вставить переменную
  save: Сохранить
  companyName: Компания
  vacancyTitle: Вакансия
  candidateFirstName: Имя соискателя
  candidateLastName: Фамилия соискателя
  myFirstName: Моё имя
  myLastName: Моя фамилия
  myPhone: Мой телефон
  myEmail: Моя почта
  interviewLink: Ссылка на интервью
  vacancyLink: Ссылка на вакансию
  textLetterFor: Текст письма для
  dataSaved: Данные сохранены
</i18n>
